import { autocompleteAddress } from '@imprint-payments/imprint-lib';
import {
  BreakpointsPx,
  colors,
  ImpButton,
  ImpContainer,
  ImpFlex,
  ImpFlexItem,
  ImpHidden,
  ImpInlineLink,
  ImpInput,
  ImpPad,
  ImpSelect,
  ImpSpacer,
  ImpText,
  useSnackbar,
} from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { useLogEventOnce } from '../../analytics/analyticsUtil';
import { setConsumerProfile } from '../../app/consumerInfoSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import {
  setAddress1Error,
  setAddress2Error,
  setCityError,
  setEmailError,
  setFnError,
  setFullDobError,
  setLnError,
  setStateError,
  setZipError,
} from '../../app/validatorSlice';
import { ANALYTICS } from '../../firebase/firebase';
import { getEmployeeId, getStoreNumber } from '../../lib/utmManager';
import { ConsumerProfile } from '../../types';
import {
  formatDateValue,
  formatDob,
  formatState,
} from '../../utils/formatUtils';
import { getPreapprovalCodeFromSession } from '../../utils/functionUtils';
import { SESSION_STORAGE } from '../../utils/sessionStorageConstants';
import {
  getValidationString,
  hideWhileTyping,
  noError,
  validateAddress,
  validateBirthDate,
  validateCity,
  validateEmail,
  validateName,
  validateState,
  validateZip,
  ValidationResponse,
} from '../../utils/validateUtils';
import { getAccountLink } from '../AlreadyAppliedText/AlreadyAppliedText';
import InfoFields from '../prove/InfoFields';
import WelcomeBackCard from '../welcomeBackCard/WelcomeBackCard';
import { InStoreLabels } from './InStoreLabels';
import { PrescreenLabel } from './PrescreenLabel';

interface PersonalInfoProps {
  nextStep: () => void;
  repeatViewing: boolean;
}

interface EditPersonalInfoProps extends PersonalInfoProps {
  saveEdits: () => void;
}

const inputWarning = [ValidationResponse.emailPotentialTypo];

export default function PersonalInfo({
  nextStep,
  repeatViewing,
}: PersonalInfoProps) {
  const [edit, setEdit] = useState(false);
  const { appScreenState } = useImpState();

  if (appScreenState.proveStatus === 'success' && !edit) {
    return <InfoFields nextStep={nextStep} editInfo={() => setEdit(true)} />;
  }

  return (
    <EditPersonalInfo
      nextStep={nextStep}
      repeatViewing={repeatViewing}
      saveEdits={() => setEdit(false)}
    />
  );
}

function EditPersonalInfo({
  nextStep,
  repeatViewing,
  saveEdits,
}: EditPersonalInfoProps) {
  const {
    language,
    merchantData,
    dispatch,
    appScreenState,
    appStatus,
    loginState,
    consumerProfile,
  } = useImpState();
  const logEventOnce = useLogEventOnce();
  const { displayTemporarySnackbar } = useSnackbar();

  useEffect(() => {
    if (appScreenState.proveStatus === 'optedOut') {
      logEventOnce(EVENT.PERSONAL_INFO_VIEWED, {
        context: 'opted_out',
      });
    } else if (appScreenState.proveStatus === 'fail') {
      logEventOnce(EVENT.PERSONAL_INFO_VIEWED, {
        context: 'not_found',
      });
    } else {
      // Edit prefilled info
      logEventOnce(EVENT.PERSONAL_INFO_EDIT_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //info states
  const [firstName, setFirstName] = useState(consumerProfile.name.firstName);
  const [disabledFirstName, setDisabledFirstName] = useState(false);
  const [lastName, setLastName] = useState(consumerProfile.name.lastName);
  const [disabledLastName, setDisabledLastName] = useState(false);
  const [email, setEmail] = useState(consumerProfile.email);
  const [disabledEmail, setDisabledEmail] = useState(false);
  const [birthDayDay, setBirthDayDay] = useState(
    consumerProfile.dob.day === 0 ? '' : String(consumerProfile.dob.day)
  );
  const [disabledBirthDayDay, setDisabledBirthDayDay] = useState(false);
  const [birthDayMonth, setBirthDayMonth] = useState(
    consumerProfile.dob.month === 0 ? '' : String(consumerProfile.dob.month)
  );
  const [disabledBirthDayMonth, setDisabledBirthDayMonth] = useState(false);
  const [birthDayYear, setBirthDayYear] = useState(
    consumerProfile.dob.year === 0 ? '' : String(consumerProfile.dob.year)
  );
  const [disabledBirthDayYear, setDisabledBirthDayYear] = useState(false);
  const birthDay = formatDob(
    `${formatDateValue(Number(birthDayMonth))}/
    ${formatDateValue(Number(birthDayDay))}/
    ${formatDateValue(Number(birthDayYear))}`
  );

  const [street, setStreet] = useState(consumerProfile.address.streetLine1);
  const [disabledStreet, setDisabledStreet] = useState(false);
  const [apartment, setApartment] = useState(
    consumerProfile.address.streetLine2
  );
  const [disabledApartment, setDisabledApartment] = useState(false);
  const [city, setCity] = useState(consumerProfile.address.city);
  const [disabledCity, setDisabledCity] = useState(false);
  const [zip, setZip] = useState(consumerProfile.address.zipCode);
  const [disabledZip, setDisabledZip] = useState(false);
  const [state, setState] = useState(consumerProfile.address.state);
  const [disabledState, setDisabledState] = useState(false);
  // assumption: whenever we have ssn9, we should be displaying full ssn field
  const [ssn] = useState(consumerProfile.ssn9 || consumerProfile.ssn4 || '');

  //view control state
  const [disabledInputField, setDisabledInputField] = useState(
    appScreenState.requireFullSSN
  );
  const [isMultiCardApplicationFlow, setIsMultiCardApplicationFlow] =
    useState(false);
  const [birthDayChecked, setBirthDayChecked] = useState(false);
  const validator: any = useAppSelector((state) => state.validatorSelector);

  useEffect(() => {
    return () => {
      // Clear prefilled phone when the component unmounts
      sessionStorage.removeItem(SESSION_STORAGE.PHONE);
    };
  }, []);

  useEffect(() => {
    if (appScreenState.showWelcomeBackCard) {
      ANALYTICS.logEvent(EVENT.RETURNING_USER_PERSONAL_INFO_VIEWED, {
        product_uuid: merchantData.productUUID,
      });
      if (loginState.multiCardUser) {
        setIsMultiCardApplicationFlow(true);
      } else {
        setIsMultiCardApplicationFlow(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appScreenState.showWelcomeBackCard, loginState.multiCardUser]);

  useEffect(() => {
    if (appScreenState.showWelcomeBackCard && loginState.multiCardUser) {
      if (consumerProfile.name.firstName !== '') {
        setDisabledFirstName(true);
      }
      if (consumerProfile.name.lastName !== '') {
        setDisabledLastName(true);
      }
      if (consumerProfile.email !== '') {
        setDisabledEmail(true);
      }
      if (consumerProfile.dob.day !== 0) {
        setDisabledBirthDayDay(true);
      }
      if (consumerProfile.dob.month !== 0) {
        setDisabledBirthDayMonth(true);
      }
      if (consumerProfile.dob.year !== 0) {
        setDisabledBirthDayYear(true);
      }
      if (consumerProfile.address.streetLine1 !== '') {
        setDisabledStreet(true);
      }
      if (consumerProfile.address.streetLine2 !== '') {
        setDisabledApartment(true);
      }
      if (consumerProfile.address.city !== '') {
        setDisabledCity(true);
      }
      if (consumerProfile.address.zipCode !== '') {
        setDisabledZip(true);
      }
      if (consumerProfile.address.state !== '') {
        setDisabledState(true);
      }
    }

    // if user reapply after evaluation, we enable input fileds,
    // same behavior as mobile (after click "edit info" in mobile)
    // for income housing, we only enable ssn4/fullSSN
    if (appStatus.reApplicationInApp && appScreenState.requireFullSSN) {
      setDisabledFirstName(true);
      setDisabledLastName(true);
      setDisabledEmail(true);
      setDisabledBirthDayDay(true);
      setDisabledBirthDayMonth(true);
      setDisabledBirthDayYear(true);
      setDisabledStreet(true);
      setDisabledApartment(true);
      setDisabledCity(true);
      setDisabledZip(true);
      setDisabledState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appScreenState.showWelcomeBackCard,
    loginState.multiCardUser,
    consumerProfile,
    appStatus.reApplicationInApp,
  ]);

  const getPersonalInfoButtonState = () => {
    if (isMultiCardApplicationFlow) return 'enabled';
    if (
      (validator.emailError === ValidationResponse.valid ||
        validator.emailError === ValidationResponse.emailPotentialTypo) &&
      (isMultiCardApplicationFlow ||
        validator.birthDateError === ValidationResponse.valid) &&
      validator.address1Error === ValidationResponse.valid &&
      (validator.address2Error === ValidationResponse.valid ||
        validator.address2Error === ValidationResponse.empty) &&
      validator.cityError === ValidationResponse.valid &&
      validator.zipError === ValidationResponse.valid &&
      validator.stateError === ValidationResponse.valid &&
      validator.fnError === ValidationResponse.valid &&
      validator.lnError === ValidationResponse.valid
      // (validator.lastFourError === ValidationResponse.valid ||
      //   validator.fullSSNError === ValidationResponse.valid)
    ) {
      return 'enabled';
    }
    return 'disabled';
  };

  useEffect(() => {
    setFirstName(consumerProfile.name.firstName);
    setLastName(consumerProfile.name.lastName);
    setEmail(consumerProfile.email);
    setBirthDayDay(
      consumerProfile.dob.day === 0 ? '' : String(consumerProfile.dob.day)
    );
    setBirthDayMonth(
      consumerProfile.dob.month === 0 ? '' : String(consumerProfile.dob.month)
    );
    setBirthDayYear(
      consumerProfile.dob.year === 0 ? '' : String(consumerProfile.dob.year)
    );
    setStreet(consumerProfile.address.streetLine1);
    setApartment(consumerProfile.address.streetLine2);
    setCity(consumerProfile.address.city);
    setZip(consumerProfile.address.zipCode);
    setState(consumerProfile.address.state);
  }, [consumerProfile]);

  useEffect(() => {
    const validationResponse = validateName(firstName, true);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setFnError(validationResponse));
    } else {
      dispatch(setFnError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName]);

  useEffect(() => {
    const validationResponse = validateName(lastName, false);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setLnError(validationResponse));
    } else {
      dispatch(setLnError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastName]);

  useEffect(() => {
    const validationResponse = validateAddress(street);
    if (validationResponse === ValidationResponse.invalidAddressPOBox) {
      ANALYTICS.logEvent(EVENT.PERSONAL_INFO_ADDRESS_INVALID, {
        product_uuid: merchantData.productUUID,
        field: 'street_address',
        text_input: street,
      });
    }
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setAddress1Error(validationResponse));
    } else {
      dispatch(setAddress1Error(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [street]);

  useEffect(() => {
    const validationResponse = validateAddress(apartment, true);
    if (validationResponse === ValidationResponse.invalidAddressPOBox) {
      ANALYTICS.logEvent(EVENT.PERSONAL_INFO_ADDRESS_INVALID, {
        product_uuid: merchantData.productUUID,
        field: 'apartment',
        text_input: apartment,
      });
    }
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setAddress2Error(validationResponse));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartment]);

  useEffect(() => {
    const validationResponse = validateCity(city);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setCityError(validationResponse));
    } else {
      dispatch(setCityError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  useEffect(() => {
    const validationResponse = validateZip(zip);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setZipError(validationResponse));
    } else {
      dispatch(setZipError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  useEffect(() => {
    const validationResponse = validateState(state);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setStateError(validationResponse));
    } else {
      dispatch(setStateError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    const validationResponse = validateBirthDate(birthDay);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setFullDobError(validationResponse));
    } else {
      dispatch(setFullDobError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthDayDay, birthDayMonth, birthDayYear]);

  useEffect(() => {
    // needEmail is a special onetime error, dont clear on page load
    if (validator.emailError === ValidationResponse.needEmail) {
      return;
    }
    const validationResponse = validateEmail(email);
    if (!hideWhileTyping.includes(validationResponse)) {
      dispatch(setEmailError(validationResponse));
    } else {
      dispatch(setEmailError(ValidationResponse.empty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleContinueClick = () => {
    const date = new Date(birthDay);
    const updatedProfile: ConsumerProfile = {
      ...consumerProfile,
      name: {
        firstName: firstName,
        lastName: lastName,
      },
      email: email,
      dob: {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      },
      address: {
        ...consumerProfile.address,
        streetLine1: street,
        streetLine2: apartment,
        city: city,
        zipCode: zip,
        state: state,
      },
    };
    if (ssn.length < 9) {
      updatedProfile.ssn4 = ssn;
    } else {
      updatedProfile.ssn9 = ssn;
      updatedProfile.ssn4 = ssn.slice(-4); // also update ssn4 if user updated ssn9
    }
    dispatch(setConsumerProfile(updatedProfile));
    if (appScreenState.proveStatus === 'success') {
      saveEdits();
      displayTemporarySnackbar({ text: language.personalInfoUpdated });
      ANALYTICS.logEvent(EVENT.PERSONAL_INFO_EDIT_SAVE_CLICKED);
    } else {
      ANALYTICS.logEvent(EVENT.PERSONAL_INFO_CONTINUE_CLICKED);
      nextStep();
    }
  };

  useEffect(() => {
    setDisabledInputField(appScreenState.requireFullSSN);
    if (!appScreenState.requireFullSSN) {
      setDisabledInputField(false);
    }
  }, [appScreenState.requireFullSSN]);

  useEffect(() => {
    const autocomplete = window.google?.maps?.places?.Autocomplete;
    const field = document.getElementById('searchAddress') as HTMLInputElement;

    if (!autocomplete || !field) {
      return;
    }

    autocompleteAddress(field, autocomplete, (address) => {
      if (address) {
        const { streetLine1, streetLine2, city, state, zipCode } = address;
        setStreet(streetLine1);
        setApartment(streetLine2);
        setCity(city);
        setState(state);
        setZip(zipCode);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google, document]);

  const birthdayView = (
    <>
      <ImpSpacer height="m" />
      <ImpText color={colors.content.onSurface} typography="body3">
        {language.birthDay}
      </ImpText>
      <ImpSpacer height="xs" />
      <ImpFlex direction="row" gap="m">
        <ImpFlexItem flex="1">
          <ImpSelect
            options={language.months.map((_, index: number) => {
              const monthNum = String(index + 1).padStart(2, '0');
              return { value: monthNum, label: monthNum };
            })}
            defaultOption={{
              value: birthDayMonth ? birthDayMonth.padStart(2, '0') : '',
              label: birthDayMonth ? birthDayMonth.padStart(2, '0') : '',
            }}
            label={language.month}
            autocomplete="bday-month"
            showError={!noError.includes(validator.birthDateError)}
            isDisabled={disabledInputField || disabledBirthDayMonth}
            onOptionChange={(value: string) => {
              setBirthDayMonth(value);
              if (birthDayChecked) {
                dispatch(
                  setFullDobError(
                    validateBirthDate(
                      formatDob(
                        `${formatDateValue(Number(value))}/
                        ${formatDateValue(Number(birthDayDay))}/
                        ${formatDateValue(Number(birthDayYear))}`
                      )
                    )
                  )
                );
              }
            }}
            instrumentation={() =>
              ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                field: 'birthday_month',
              })
            }
          />
        </ImpFlexItem>
        <ImpFlexItem flex="1">
          <ImpInput
            label={language.day}
            value={birthDayDay}
            autocomplete="bday-day"
            showError={!noError.includes(validator.birthDateError)}
            errorMessage={''}
            isDisabled={disabledInputField || disabledBirthDayDay}
            maxLength={2}
            onChange={(e) => setBirthDayDay(e.target.value)}
            instrumentation={() =>
              ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                field: 'birthday_day',
              })
            }
            onBlur={() => {
              if (birthDayChecked) {
                dispatch(setFullDobError(validateBirthDate(birthDay)));
              }
            }}
            numeric
          />
        </ImpFlexItem>
        <ImpFlexItem flex="1">
          <ImpInput
            label={language.year}
            value={birthDayYear}
            autocomplete="bday-year"
            showError={!noError.includes(validator.birthDateError)}
            errorMessage={''}
            isDisabled={disabledInputField || disabledBirthDayYear}
            maxLength={4}
            onChange={(e) => setBirthDayYear(e.target.value)}
            instrumentation={() =>
              ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                field: 'birthday_year',
              })
            }
            onBlur={() => {
              setBirthDayChecked(true);
              dispatch(setFullDobError(validateBirthDate(birthDay)));
            }}
            numeric
          />
        </ImpFlexItem>
      </ImpFlex>
      {/* do not show error message if the user has not finished entering birthday */}
      {!noError.includes(validator.birthDateError) && (
        <>
          <ImpSpacer height="xs" />
          <ImpText typography="body3" color={colors.content.onBackgroundError}>
            {getValidationString(validator.birthDateError, language)}
          </ImpText>
        </>
      )}
    </>
  );

  const AddressView = (
    <ImpContainer width="100%">
      <ImpFlex direction="column" gap="m" align="normal" fluid>
        <ImpFlexItem flexGrow={1}>
          <ImpInput
            id="searchAddress"
            label={
              isMultiCardApplicationFlow
                ? language.homeAddress
                : language.street
            }
            value={street}
            autocomplete="address-line1"
            showError={!noError.includes(validator.address1Error)}
            errorMessage={getValidationString(
              validator.address1Error,
              language
            )}
            isDisabled={disabledInputField || disabledStreet}
            onChange={(e) => setStreet(e.target.value)}
            instrumentation={() =>
              ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                field: 'street_address',
              })
            }
            onBlur={() =>
              dispatch(setAddress1Error(validateAddress(street, false)))
            }
            caption={language.personalInfoAddressInstruction}
            maxWidth="100%"
          />
        </ImpFlexItem>
        {!isMultiCardApplicationFlow && (
          <>
            <ImpInput
              id="apartment"
              label={language.apartment}
              value={apartment}
              autocomplete="address-line2"
              showError={!noError.includes(validator.address2Error)}
              errorMessage={getValidationString(
                validator.address2Error,
                language
              )}
              isDisabled={disabledInputField || disabledApartment}
              onChange={(e) => setApartment(e.target.value)}
              instrumentation={() =>
                ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                  field: 'apartment',
                })
              }
              onBlur={() =>
                dispatch(setAddress2Error(validateAddress(apartment, true)))
              }
              maxWidth="100%"
            />
            <ImpInput
              id="city"
              label={language.city}
              value={city}
              autocomplete="address-level2"
              showError={!noError.includes(validator.cityError)}
              errorMessage={getValidationString(validator.cityError, language)}
              isDisabled={disabledInputField || disabledCity}
              onChange={(e) => setCity(e.target.value)}
              instrumentation={() =>
                ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                  field: 'city',
                })
              }
              onBlur={() => dispatch(setCityError(validateCity(city)))}
              maxWidth="100%"
            />
            <ImpFlex direction="row" gap="m">
              <ImpFlexItem flexGrow={1}>
                <ImpInput
                  maxLength={5}
                  id="zip"
                  label={language.zip}
                  value={zip}
                  autocomplete="postal-code"
                  showError={!noError.includes(validator.zipError)}
                  errorMessage={getValidationString(
                    validator.zipError,
                    language
                  )}
                  isDisabled={disabledInputField || disabledZip}
                  onChange={(e) => setZip(e.target.value)}
                  instrumentation={() =>
                    ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                      field: 'zip_code',
                    })
                  }
                  onBlur={() => dispatch(setZipError(validateZip(zip)))}
                  numeric
                />
              </ImpFlexItem>
              <ImpFlexItem flexGrow={1}>
                <ImpSelect
                  id="option"
                  maxLength={2}
                  defaultOption={{ value: state, label: state }}
                  options={language.stateOption.map((val: string) => {
                    return { value: val, label: val };
                  })}
                  label={language.state}
                  autocomplete="address-level1"
                  showError={!noError.includes(validator.stateError)}
                  errorMessage={getValidationString(
                    validator.stateError,
                    language
                  )}
                  isDisabled={disabledInputField || disabledState}
                  onOptionChange={(value: string) => {
                    setState(formatState(value.toUpperCase()));
                  }}
                  instrumentation={() => {
                    ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                      field: 'state',
                    });
                  }}
                />
              </ImpFlexItem>
            </ImpFlex>
          </>
        )}
      </ImpFlex>
    </ImpContainer>
  );

  const emailOnBlur = () => {
    const validationResponse = validateEmail(email);
    dispatch(setEmailError(validationResponse));
    switch (validationResponse) {
      case ValidationResponse.emailPotentialTypo: {
        ANALYTICS.logEvent(EVENT.PERSONAL_INFO_EMAIL_WARNING, {
          product_uuid: merchantData.productUUID,
          text_input: email,
        });
        return;
      }
      case ValidationResponse.invalidEmail: {
        ANALYTICS.logEvent(EVENT.PERSONAL_INFO_EMAIL_INVALID, {
          product_uuid: merchantData.productUUID,
          text_input: email,
        });
        return;
      }
    }
  };

  const preapprovalCode = getPreapprovalCodeFromSession();
  const storeNum = getStoreNumber(merchantData.productUUID);
  const employeeId = getEmployeeId(merchantData.productUUID);

  let subtitleText = '';

  if (appScreenState.proveStatus === 'success') {
    subtitleText = 'Check and edit your information below';
  } else if (appScreenState.proveStatus === 'fail') {
    subtitleText = language.unableToAutoFill;
  } else if (repeatViewing || appScreenState.proveStatus === 'optedOut') {
    subtitleText = ''; // optedOut case, same as the default empty string
  } else if (appScreenState.proveStatus === 'noEmail') {
    subtitleText =
      'We found the information below. Please check that everything is correct and complete the rest before continuing.';
  }

  return (
    <ImpContainer width="100%" maxWidth="368px" textAlign="center">
      <ImpPad paddingTop={'xl'} paddingBottom={'xl'}>
        {appScreenState.showWelcomeBackCard &&
          consumerProfile.name.firstName && (
            <WelcomeBackCard firstName={consumerProfile.name.firstName} />
          )}

        <ImpFlex
          fluid
          direction={'column'}
          align={'center'}
          justify={'center'}
          gap={'xs'}
        >
          <ImpText typography={'headline3'}>{language.personalInfo}</ImpText>
          <ImpText typography={'body2'} color={colors.content.onSurfaceVariant}>
            {subtitleText}
          </ImpText>
        </ImpFlex>

        <ImpSpacer height={'xl'} />
        {preapprovalCode && <PrescreenLabel code={preapprovalCode} />}
        {(storeNum || employeeId) && (
          <InStoreLabels storeNum={storeNum} employeeId={employeeId} />
        )}
        <ImpContainer width="100%">
          <ImpFlex direction="row" gap="m">
            <ImpFlexItem flex="1">
              <ImpInput
                label={language.firstName}
                value={firstName}
                autocomplete="given-name"
                showError={!noError.includes(validator.fnError)}
                errorMessage={getValidationString(validator.fnError, language)}
                isDisabled={disabledInputField || disabledFirstName}
                onChange={(e) => setFirstName(e.target.value)}
                instrumentation={() =>
                  ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                    field: 'first_name',
                  })
                }
                onBlur={() =>
                  dispatch(setFnError(validateName(firstName, true)))
                }
              />
            </ImpFlexItem>
            <ImpFlexItem flex="1">
              <ImpInput
                label={language.lastName}
                value={lastName}
                autocomplete="family-name"
                showError={!noError.includes(validator.lnError)}
                errorMessage={getValidationString(validator.lnError, language)}
                isDisabled={disabledInputField || disabledLastName}
                onChange={(e) => setLastName(e.target.value)}
                instrumentation={() =>
                  ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
                    field: 'last_name',
                  })
                }
                onBlur={() =>
                  dispatch(setLnError(validateName(lastName, false)))
                }
              />
            </ImpFlexItem>
          </ImpFlex>
        </ImpContainer>
        {!isMultiCardApplicationFlow && birthdayView}
        <ImpSpacer height="m" />
        <ImpInput
          label={language.email}
          value={email}
          type="email"
          autocomplete="email"
          showError={!noError.includes(validator.emailError)}
          showWarning={inputWarning.includes(validator.emailError)}
          errorMessage={getValidationString(validator.emailError, language)}
          isDisabled={disabledInputField || disabledEmail}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          onFocus={() => {
            if (validator.emailError === ValidationResponse.needEmail)
              dispatch(setEmailError(ValidationResponse.empty));
          }}
          instrumentation={() =>
            ANALYTICS.logEvent(EVENT.PERSONAL_INFO_FORM_FILLED_OUT, {
              field: 'email',
            })
          }
          onBlur={() => emailOnBlur()}
          maxWidth="100%"
        />
        <ImpSpacer height="m" />
        {AddressView}
        <ImpSpacer height="xl" />
        <ImpButton
          variant="primary"
          buttonStyle="branded"
          size="fill"
          state={getPersonalInfoButtonState()}
          onClick={handleContinueClick}
        >
          {appScreenState.proveStatus === 'success'
            ? 'Save'
            : language.continueBtn}
        </ImpButton>

        <ImpHidden hideAbove={BreakpointsPx.Mobile}>
          <ImpSpacer height="l" />
          <ImpContainer width="100%">
            <ImpFlex fluid justify="center">
              <ImpInlineLink
                url={getAccountLink(window.location.hostname.toLowerCase())}
                color="var(--merchant-color)"
                onClick={() =>
                  ANALYTICS.logEvent(
                    EVENT.APPLICATION_ALREADY_APPLIED_SECONDARY_CLICKED
                  )
                }
              >
                <ImpText typography="body2" color="var(--merchant-color)">
                  {language.checkApplicationStatusHeader}
                </ImpText>
              </ImpInlineLink>
            </ImpFlex>
          </ImpContainer>
        </ImpHidden>
      </ImpPad>
    </ImpContainer>
  );
}
